import { Filter, FilterOperator } from '@repo/api/data-api/hooks'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import LZString from 'lz-string'

// Constants for URL parameters
const VIEW_PARAM = 'vw'
const ORGANIZATION_VIEW_VALUE = 'organization'

// Filter view enum (re-exported from context)
export enum FilterView {
  SPECIMEN = 'specimen',
  ORGANIZATION = 'organization',
}

/**
 * URL serialization utilities for filters
 * Handles encoding/decoding filter state to/from URL search parameters
 * Uses LZ-string compression with compact serialization to minimize URL size
 */

const FILTERS_PARAM = 'filters'
const MAX_URL_LENGTH = 2000
const MAX_FILTERS = 50

/**
 * Compact filter interface for URL serialization
 * Reduces property names to single characters
 */
interface CompactFilter {
  k: string // key
  v: any // value
  o: FilterOperator // operator
  n: boolean // negated
}

function toCompactFilters(filters: Filter[]): CompactFilter[] {
  return filters.map(({ key, value, operator, negated }) => ({
    k: key,
    v: value,
    o: operator,
    n: negated,
  }))
}

function fromCompactFilters(compactFilters: CompactFilter[]): Filter[] {
  return compactFilters.map(({ k, v, o, n }) => ({
    key: k,
    value: v,
    operator: o,
    negated: n,
  }))
}

function groupFiltersByKey(filters: Filter[]): CompactFilter[] {
  const grouped = new Map<string, { values: any[]; operator: FilterOperator; negated: boolean }>()

  for (const { key, value, operator, negated } of filters) {
    const groupKey = `${key}:${negated}`
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, { values: [], operator, negated })
    }

    const group = grouped.get(groupKey)!
    if (Array.isArray(value)) {
      group.values.push(...value)
    } else {
      group.values.push(value)
    }
  }

  return Array.from(grouped.entries()).map(([key, data]) => ({
    k: key.split(':')[0],
    v: data.values.length === 1 ? data.values[0] : data.values,
    o: data.operator,
    n: data.negated,
  }))
}

export function serializeFiltersToUrl(filters: Filter[]): string {
  if (filters.length === 0) return ''

  try {
    const limitedFilters = filters.slice(0, MAX_FILTERS)
    const groupedFilters = groupFiltersByKey(limitedFilters)
    const compactFilters = toCompactFilters(limitedFilters)

    // Use the more compact format
    const finalFilters = groupedFilters.length < compactFilters.length ? groupedFilters : compactFilters
    const compressed = compressFilters(fromCompactFilters(finalFilters))

    return compressed || ''
  } catch (error) {
    console.warn('Failed to serialize filters to URL:', error)
    return ''
  }
}

function compressFilters(filters: Filter[]): string | null {
  try {
    const compactFilters = toCompactFilters(filters)
    const groupedFilters = groupFiltersByKey(filters)
    const finalFilters = groupedFilters.length < compactFilters.length ? groupedFilters : compactFilters
    const stateString = JSON.stringify(finalFilters)

    // Try LZ-string compression first
    const base64Compressed = LZString.compressToBase64(stateString)

    if (base64Compressed.length <= MAX_URL_LENGTH) {
      return base64Compressed
    }

    // LZ compression too large, try base64 fallback
    console.warn(`LZ compressed URL (${base64Compressed.length} chars) exceeds limit, trying base64 fallback`)
    const fallbackEncoded = btoa(encodeURIComponent(stateString))

    if (fallbackEncoded.length <= MAX_URL_LENGTH) {
      return fallbackEncoded
    }

    console.warn(`Both compression methods exceed limit (${MAX_URL_LENGTH} chars). Aborting URL update.`)
    return null
  } catch (error) {
    console.warn('Compression failed, trying base64 fallback:', error)
    const fallbackEncoded = btoa(encodeURIComponent(JSON.stringify(filters)))

    if (fallbackEncoded.length <= MAX_URL_LENGTH) {
      return fallbackEncoded
    }

    console.warn(`Fallback URL (${fallbackEncoded.length} chars) also exceeds limit. Aborting URL update.`)
    return null
  }
}

export function deserializeFiltersFromUrl(encodedFilters: string): Filter[] {
  if (!encodedFilters) return []

  try {
    // Try LZ-string decompression first
    const decompressed = LZString.decompressFromBase64(encodedFilters)
    if (decompressed) {
      const data = JSON.parse(decompressed)
      return validateFilters(fromCompactFilters(data as CompactFilter[]))
    }
  } catch {
    // Fall back to base64 decoding
  }

  try {
    // Fallback to base64 decoding for legacy URLs
    const decoded = decodeURIComponent(atob(encodedFilters))
    const data = JSON.parse(decoded)

    if (!Array.isArray(data)) {
      console.warn('Deserialized data is not an array:', data)
      return []
    }

    // Check if it's compact format or legacy format
    const compactFilters =
      data.length > 0 && 'k' in data[0] ? (data as CompactFilter[]) : toCompactFilters(data as Filter[])

    return validateFilters(fromCompactFilters(compactFilters))
  } catch (error) {
    console.warn('Failed to deserialize filters from URL:', error)
    return []
  }
}

export function updateUrlWithFilters(filters: Filter[], router: AppRouterInstance, pathname: string): void {
  try {
    const searchParams = new URLSearchParams(window.location.search)

    if (filters.length === 0) {
      searchParams.delete(FILTERS_PARAM)
    } else {
      const serializedFilters = serializeFiltersToUrl(filters)
      if (!serializedFilters) {
        console.warn('Filters exceed URL length limit. URL will not be updated.')
        return
      }
      searchParams.set(FILTERS_PARAM, serializedFilters)
    }

    const newUrl = `${pathname}?${searchParams.toString()}`
    router.replace(newUrl, { scroll: false })
  } catch (error) {
    console.error('Failed to update URL with filters:', error)

    // Fallback: clear filters and update URL
    try {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete(FILTERS_PARAM)
      router.replace(`${pathname}?${searchParams.toString()}`, { scroll: false })
    } catch (fallbackError) {
      console.error('Failed to clear URL filters:', fallbackError)
    }
  }
}

export function getFiltersFromUrl(): Filter[] {
  if (typeof window === 'undefined') return []

  try {
    const searchParams = new URLSearchParams(window.location.search)
    const encodedFilters = searchParams.get(FILTERS_PARAM)
    return encodedFilters ? deserializeFiltersFromUrl(encodedFilters) : []
  } catch (error) {
    console.warn('Failed to get filters from URL:', error)
    return []
  }
}

export function isValidFilter(filter: any): filter is Filter {
  return (
    filter &&
    typeof filter === 'object' &&
    typeof filter.key === 'string' &&
    filter.value !== undefined &&
    typeof filter.operator === 'string' &&
    typeof filter.negated === 'boolean'
  )
}

export function validateFilters(filters: any[]): Filter[] {
  if (!Array.isArray(filters)) return []

  return filters
    .filter(isValidFilter)
    .slice(0, MAX_FILTERS)
    .map(({ key, value, operator, negated }) => ({ key, value, operator, negated }))
}

/**
 * Helper function to get filter view from URL parameter
 */
export function getViewFromUrl(): FilterView {
  if (typeof window === 'undefined') return FilterView.SPECIMEN

  try {
    const searchParams = new URLSearchParams(window.location.search)
    const viewParam = searchParams.get(VIEW_PARAM)
    return viewParam === ORGANIZATION_VIEW_VALUE ? FilterView.ORGANIZATION : FilterView.SPECIMEN
  } catch (error) {
    console.warn('Failed to get view from URL:', error)
    return FilterView.SPECIMEN
  }
}

/**
 * Helper function to update URL with view parameter
 */
export function updateUrlWithView(
  view: FilterView,
  router: AppRouterInstance,
  pathname: string
): void {
  try {
    const params = new URLSearchParams(window.location.search)
    if (view === FilterView.ORGANIZATION) {
      params.set(VIEW_PARAM, ORGANIZATION_VIEW_VALUE)
    } else {
      params.delete(VIEW_PARAM) // Remove parameter for default SPECIMEN view
    }

    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  } catch (error) {
    console.error('Failed to update URL with view:', error)
  }
}
